import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Lock, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Layout } from '../components/ui/Layout';
import { 
  Form, 
  FormGroup, 
  Label, 
  Input, 
  Button, 
  ErrorMessage 
} from '../components/ui/Form';
import { theme } from '../styles/theme';
import { authService } from '../services/authService';

const InputContainer = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  color: ${theme.colors.textTertiary};
  z-index: 1;
`;

const StyledInput = styled(Input)`
  padding-left: ${theme.spacing['3xl']};
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing.md};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.borderRadius.md};
  transition: color ${theme.transitions.fast}, background-color ${theme.transitions.fast}, transform ${theme.transitions.fast};
  
  &:hover {
    color: ${theme.colors.primaryLight};
    background-color: rgba(255, 255, 255, 0.08);
    transform: translateY(-50%) translateY(-1px);
  }
`;

const PasswordStrength = styled.div<{ $strength: number }>`
  height: 4px;
  background-color: ${theme.colors.gray200};
  border-radius: ${theme.borderRadius.full};
  margin-top: ${theme.spacing.sm};
  overflow: hidden;
  
  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $strength }) => ($strength / 4) * 100}%;
    background-color: ${({ $strength }) => {
      if ($strength <= 1) return theme.colors.error;
      if ($strength <= 2) return theme.colors.warning;
      if ($strength <= 3) return theme.colors.info;
      return theme.colors.success;
    }};
    transition: all ${theme.transitions.normal};
  }
`;

const PasswordStrengthText = styled.div<{ $strength: number }>`
  font-size: ${theme.fontSizes.xs};
  margin-top: ${theme.spacing.xs};
  color: ${({ $strength }) => {
    if ($strength <= 1) return theme.colors.error;
    if ($strength <= 2) return theme.colors.warning;
    if ($strength <= 3) return theme.colors.info;
    return theme.colors.success;
  }};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} 0;
`;

const ErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${theme.colors.error};
  border-radius: ${theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing.lg};
  color: ${theme.colors.white};
`;

const ErrorTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const ErrorDescription = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  line-height: 1.5;
`;

const validationSchema = yup.object({
  newPassword: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .matches(/(?=.*[a-z])/, 'Password must contain at least one lowercase letter')
    .matches(/(?=.*[A-Z])/, 'Password must contain at least one uppercase letter')
    .matches(/(?=.*\d)/, 'Password must contain at least one number'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 6) strength++;
  if (/(?=.*[a-z])/.test(password)) strength++;
  if (/(?=.*[A-Z])/.test(password)) strength++;
  if (/(?=.*\d)/.test(password)) strength++;
  if (/(?=.*[!@#$%^&*])/.test(password)) strength++;
  return Math.min(strength, 4);
};

const getPasswordStrengthText = (strength: number): string => {
  if (strength <= 1) return 'Weak';
  if (strength <= 2) return 'Fair';
  if (strength <= 3) return 'Good';
  return 'Strong';
};

export const ResetPassword: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordForm>({
    resolver: yupResolver(validationSchema),
  });

  const newPassword = watch('newPassword');

  useEffect(() => {
    if (!token) {
      setIsValidToken(false);
      return;
    }
    
    // Here you would typically validate the token with your API
    // For now, we'll assume it's valid if it exists
    setIsValidToken(true);
  }, [token]);

  useEffect(() => {
    if (newPassword) {
      setPasswordStrength(calculatePasswordStrength(newPassword));
    } else {
      setPasswordStrength(0);
    }
  }, [newPassword]);

  const onSubmit = async (data: ResetPasswordForm) => {
    if (!token) return;
    
    setIsLoading(true);
    
    try {
      const response = await authService.changePassword(token, data.newPassword);
      
      if (response.success) {
        toast.success('Password reset successful! Please sign in with your new password.');
        navigate('/login');
      } else {
        toast.error(response.error || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidToken === null) {
    return (
      <Layout
        title="Reset Password"
        subtitle="Validating reset token..."
      >
        <div style={{ textAlign: 'center', padding: `${theme.spacing.xl} 0` }}>
          <div>Loading...</div>
        </div>
      </Layout>
    );
  }

  if (isValidToken === false) {
    return (
      <Layout
        title="Invalid Reset Link"
        subtitle="This password reset link is not valid"
      >
        <ErrorContainer>
          <ErrorIcon>
            <AlertCircle size={32} />
          </ErrorIcon>
          <ErrorTitle>Invalid or Expired Link</ErrorTitle>
          <ErrorDescription>
            This password reset link is either invalid or has expired. 
            Please request a new password reset link.
          </ErrorDescription>
          
          <Button 
            as="a"
            href="/forgot-password"
            $fullWidth
          >
            Request New Reset Link
          </Button>
        </ErrorContainer>
      </Layout>
    );
  }

  return (
    <Layout
      title="Reset Password"
      subtitle="Enter your new password"
    >
      <Form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Label htmlFor="newPassword">New Password</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="newPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your new password"
              $hasError={!!errors.newPassword}
              {...register('newPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {newPassword && (
            <>
              <PasswordStrength $strength={passwordStrength} />
              <PasswordStrengthText $strength={passwordStrength}>
                Password strength: {getPasswordStrengthText(passwordStrength)}
              </PasswordStrengthText>
            </>
          )}
          {errors.newPassword && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.newPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="confirmPassword">Confirm New Password</Label>
          <InputContainer>
            <InputIcon>
              <Lock size={20} />
            </InputIcon>
            <StyledInput
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm your new password"
              $hasError={!!errors.confirmPassword}
              {...register('confirmPassword')}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </PasswordToggle>
          </InputContainer>
          {errors.confirmPassword && (
            <ErrorMessage>
              <AlertCircle size={16} />
              {errors.confirmPassword.message}
            </ErrorMessage>
          )}
        </FormGroup>

        <Button 
          type="submit" 
          $fullWidth 
          $loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Form>
    </Layout>
  );
};
